import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { LocationService, Position } from './location.service';
import { AuthService } from './auth.service';
import { WebSocketService } from './websocket.service';

export interface QRData {
  localId: string;
  tableId?: string;
  type: 'restaurant' | 'table' | 'menu';
  version: string;
  timestamp?: string;
  signature?: string;
}

export interface RestaurantInfo {
  id: string;
  name: string;
  address: string;
  location: {
    lat: number;
    lng: number;
  };
  phone?: string;
  website?: string;
  features: {
    menuEnabled: boolean;
    orderingEnabled: boolean;
    tableServiceEnabled: boolean;
    barOrderingEnabled: boolean;
    geolocationRequired: boolean;
    posIntegration: boolean;
  };
  openingHours: {
    [key: string]: {
      open: string;
      close: string;
      closed: boolean;
    };
  };
  menu?: MenuData;
  tables?: TableInfo[];
}

export interface TableInfo {
  id: string;
  number: string;
  seats: number;
  location?: string;
  active: boolean;
}

export interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category?: string;
  category_id?: string;
  image?: string;
  images?: string[];
  allergens: string[];
  dietary_info?: string[];
  available: boolean;
  preparation_time?: number;
  preparationTime?: number; // Keep for backward compatibility
  popularity_score?: number;
  calories?: number;
  spice_level?: number;
  ingredients?: string[];
  customizations?: any[];
  original_price?: number;
  tags?: string[];
}

export interface MenuCategory {
  id: string;
  name: string;
  description?: string;
  display_order: number;
  visible: boolean;
  parent_id?: number | null; // Add parent_id for hierarchical structure
  items: MenuItem[];
}

export interface MenuData {
  id: string;
  restaurant_id: string;
  name: string;
  description?: string;
  currency: string;
  language: string;
  categories: MenuCategory[];
  products?: MenuItem[]; // Add products array for compatibility
  updated_at: string;
  version: number;
}

export interface QRScanResult {
  success: boolean;
  type?: 'restaurant' | 'table' | 'menu' | 'invalid';
  restaurant?: RestaurantInfo;
  table?: TableInfo;
  menu?: MenuData; // Add complete menu data
  session?: {
    session_id: string;
    table_number?: string;
    expires_at: string;
    permissions: string[];
    max_distance: number;
  };
  validation?: {
    is_valid: boolean;
    restaurant_id?: string;
    table_number?: string;
    menu_mode: string;
    distance_to_restaurant?: number;
    validation_errors: string[];
    session_token?: string;
  };
  actions?: {
    canViewMenu: boolean;
    canOrderToBar: boolean;
    canOrderToTable: boolean;
    requiresGeolocation: boolean;
    requiresTableSelection: boolean;
  };
  message?: string;
  error?: string;
}

export interface OrderValidation {
  isValid: boolean;
  requirements: {
    geolocationCheck: boolean;
    tableCheck: boolean;
    posConnection: boolean;
  };
  errors: string[];
  location?: {
    userLat: number;
    userLng: number;
    restaurantLat: number;
    restaurantLng: number;
    distance: number;
    isWithinRange: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class QrService {
  private readonly API_URL = '/api/qr';
  private readonly MAX_DISTANCE_METERS = 100; // 100 metros máximo del restaurante

  // Real-time menu updates
  private currentMenuSubject = new BehaviorSubject<MenuData | null>(null);
  public currentMenu$ = this.currentMenuSubject.asObservable();

  constructor(
    private http: HttpClient,
    private locationService: LocationService,
    private authService: AuthService,
    private webSocketService: WebSocketService
  ) {
    // Subscribe to real-time menu updates
    this.webSocketService.menuUpdates$.subscribe((menuUpdate) => {
      console.log('🍽️ Received real-time menu update:', menuUpdate);
      const menuData = this.convertExternalMenuToMenuData(menuUpdate.data.menu);
      this.currentMenuSubject.next(menuData);
    });
  }

  /**
   * Obtener headers de autenticación
   */
  private getAuthHeaders(): HttpHeaders {
    // Debug completo del estado de autenticación
    const currentUser = this.authService.getCurrentUser();
    const token = this.authService.getToken();
    
    
    // Verificar si el usuario está autenticado
    if (!currentUser && !token) {
      console.warn('⚠️ Usuario no autenticado. Redirigir a login...');
      // Podrías emitir un evento o redirigir aquí si es necesario
    }
    
    if (token) {
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${token}`
      });
      return headers;
    } else {
    }
    return new HttpHeaders();
  }

  /**
   * Escanear y procesar código QR
   */
  scanQRCode(qrData: string): Observable<QRScanResult> {
    return this.http.post<QRScanResult>(`${this.API_URL}/scan`, {
      qrData
    }).pipe(
      map(result => this.processQRResult(result)),
      catchError(error => {
        console.error('Error escaneando QR:', error);
        return of({
          success: false,
          type: 'invalid' as const,
          actions: {
            canViewMenu: false,
            canOrderToBar: false,
            canOrderToTable: false,
            requiresGeolocation: false,
            requiresTableSelection: false
          },
          error: 'Error al procesar el código QR'
        } as QRScanResult);
      })
    );
  }

  /**
   * Obtener información del restaurante
   */
  getRestaurantInfo(localId: string): Observable<RestaurantInfo> {
    // Use the new menu API endpoint with authentication (updated)
    const headers = this.getAuthHeaders();
    return this.http.get<any>(`/api/v1/menu/${localId}`, { headers })
      .pipe(
        map(response => {
          if (response.success && response.data) {
            const menuData = response.data;
            // Convert menu API response to RestaurantInfo format
            return {
              id: menuData.company_id.toString(),
              name: menuData.company_name,
              address: 'Dirección no disponible', // Menu API doesn't include address
              location: {
                lat: 0, // Menu API doesn't include location
                lng: 0
              },
              phone: undefined,
              website: undefined,
              features: {
                menuEnabled: true,
                orderingEnabled: menuData.allow_orders,
                tableServiceEnabled: false,
                barOrderingEnabled: menuData.allow_orders,
                geolocationRequired: false,
                posIntegration: false
              },
              openingHours: {},
              menu: this.convertMenuApiToMenuData(menuData)
            } as RestaurantInfo;
          } else {
            throw new Error(response.error || 'Restaurante no encontrado');
          }
        }),
        catchError(error => {
          console.error('Error obteniendo info del restaurante:', error);
          throw error;
        })
      );
  }

  /**
   * Inicializar conexión WebSocket para menús en tiempo real
   */
  initializeRealTimeMenus(): void {
    const token = this.authService.getToken();
    if (token) {
      this.webSocketService.connect(token);
    }
  }

  /**
   * Suscribirse a actualizaciones de menú en tiempo real
   */
  subscribeToRealTimeMenu(companyId: number): void {
    if (!this.webSocketService.isConnected()) {
      const token = this.authService.getToken();
      if (token) {
        this.webSocketService.connect(token);
      }
    }
    this.webSocketService.subscribeToMenu(companyId);
  }

  /**
   * Desconectar del servicio de menús en tiempo real
   */
  disconnectRealTimeMenus(): void {
    this.webSocketService.disconnect();
  }

  /**
   * Convertir menú externo a formato MenuData interno
   */
  private convertExternalMenuToMenuData(externalMenu: any): MenuData {
    const categories: MenuCategory[] = (externalMenu.categories || []).map((cat: any, index: number) => ({
      id: `cat_${index}`,
      name: cat.name,
      description: cat.description,
      display_order: index,
      visible: true,
      items: (cat.products || []).map((product: any, prodIndex: number) => ({
        id: `item_${index}_${prodIndex}`,
        name: product.name,
        description: product.description || '',
        price: product.price,
        category: product.category,
        category_id: `cat_${index}`,
        allergens: product.allergies || [],
        dietary_info: product.dietary_info || [],
        available: product.active !== false,
        ingredients: product.ingredients || [],
        image: undefined,
        images: [],
        preparation_time: undefined,
        popularity_score: undefined,
        calories: undefined,
        spice_level: undefined,
        customizations: [],
        original_price: undefined,
        tags: []
      } as MenuItem))
    } as MenuCategory));

    return {
      id: `menu_${externalMenu.company_id || 'unknown'}`,
      restaurant_id: (externalMenu.company_id || 0).toString(),
      name: `Menú de ${externalMenu.company_name || 'Restaurante'}`,
      description: `Menú actualizado de ${externalMenu.company_name || 'Restaurante'}`,
      currency: externalMenu.currency || 'EUR',
      language: 'es',
      categories,
      updated_at: externalMenu.sync_timestamp || new Date().toISOString(),
      version: parseInt(externalMenu.version) || 1
    } as MenuData;
  }

  /**
   * Obtener menú del restaurante
   */
  getMenu(localId: string): Observable<MenuData> {
    // Use the new menu API endpoint with authentication
    const headers = this.getAuthHeaders();
    return this.http.get<any>(`/api/v1/menu/${localId}`, { headers })
      .pipe(
        map(response => {
          if (response.success && response.data) {
            const menuData = this.convertMenuApiToMenuData(response.data);
            
            // Enable real-time updates for this menu
            const companyId = parseInt(localId);
            if (!isNaN(companyId)) {
              this.subscribeToRealTimeMenu(companyId);
            }
            
            // Update current menu subject
            this.currentMenuSubject.next(menuData);
            
            return menuData;
          } else {
            throw new Error(response.error || 'Menú no disponible');
          }
        }),
        catchError(error => {
          console.error('Error obteniendo menú:', error);
          return of({
            id: '',
            restaurant_id: localId,
            name: 'Menú no disponible',
            currency: 'EUR',
            language: 'es',
            categories: [],
            updated_at: new Date().toISOString(),
            version: 1
          });
        })
      );
  }

  /**
   * Validar si se puede realizar un pedido
   */
  async validateOrder(localId: string, tableId?: string): Promise<OrderValidation> {
    try {
      // Obtener información del restaurante
      const restaurant = await this.getRestaurantInfo(localId).toPromise();
      
      if (!restaurant) {
        return {
          isValid: false,
          requirements: {
            geolocationCheck: false,
            tableCheck: false,
            posConnection: false
          },
          errors: ['Restaurante no encontrado']
        };
      }

      const validation: OrderValidation = {
        isValid: true,
        requirements: {
          geolocationCheck: false,
          tableCheck: false,
          posConnection: false
        },
        errors: []
      };

      // 1. Verificar geolocalización si es requerida
      if (restaurant.features.geolocationRequired) {
        try {
          const userPosition = await this.locationService.getCurrentPosition().toPromise();
          if (userPosition) {
            const distance = this.calculateDistance(
              userPosition.latitude,
              userPosition.longitude,
              restaurant.location.lat,
              restaurant.location.lng
            );

            validation.location = {
              userLat: userPosition.latitude,
              userLng: userPosition.longitude,
              restaurantLat: restaurant.location.lat,
              restaurantLng: restaurant.location.lng,
              distance,
              isWithinRange: distance <= this.MAX_DISTANCE_METERS
            };

            if (distance > this.MAX_DISTANCE_METERS) {
              validation.isValid = false;
              validation.errors.push(`Debes estar cerca del restaurante (distancia: ${Math.round(distance)}m)`);
            } else {
              validation.requirements.geolocationCheck = true;
            }
          }
        } catch (error) {
          validation.isValid = false;
          validation.errors.push('No se pudo verificar tu ubicación');
        }
      } else {
        validation.requirements.geolocationCheck = true;
      }

      // 2. Verificar mesa si es requerida
      if (restaurant.features.tableServiceEnabled) {
        if (tableId) {
          const table = restaurant.tables?.find(t => t.id === tableId);
          if (table && table.active) {
            validation.requirements.tableCheck = true;
          } else {
            validation.isValid = false;
            validation.errors.push('Mesa no válida o inactiva');
          }
        } else {
          // Si no hay tableId pero se requiere, permitir selección manual
          validation.requirements.tableCheck = false;
        }
      } else {
        validation.requirements.tableCheck = true;
      }

      // 3. Verificar conexión POS
      if (restaurant.features.posIntegration) {
        const posStatus = await this.checkPOSConnection(localId);
        validation.requirements.posConnection = posStatus;
        if (!posStatus) {
          validation.isValid = false;
          validation.errors.push('Sistema de pedidos temporalmente no disponible');
        }
      } else {
        validation.requirements.posConnection = true;
      }

      return validation;

    } catch (error) {
      return {
        isValid: false,
        requirements: {
          geolocationCheck: false,
          tableCheck: false,
          posConnection: false
        },
        errors: ['Error al validar pedido']
      };
    }
  }

  /**
   * Realizar pedido a barra (enviar al POS)
   */
  createBarOrder(localId: string, items: {
    menuItemId: string;
    quantity: number;
    notes?: string;
  }[], options: {
    tableId?: string;
    customerName?: string;
    specialRequests?: string;
  }): Observable<any> {
    return this.http.post(`${this.API_URL}/order/bar`, {
      localId,
      items,
      options,
      timestamp: new Date().toISOString()
    }).pipe(
      catchError(error => {
        console.error('Error creando pedido a barra:', error);
        throw error;
      })
    );
  }

  /**
   * Obtener estado del pedido
   */
  getOrderStatus(orderId: string): Observable<any> {
    return this.http.get(`${this.API_URL}/order/${orderId}/status`)
      .pipe(
        catchError(error => {
          console.error('Error obteniendo estado del pedido:', error);
          throw error;
        })
      );
  }

  /**
   * Verificar si el QR es válido
   */
  isValidQRFormat(qrData: string): boolean {
    try {
      // Formatos soportados:
      // 1. JSON: {"localId":"123","tableId":"5","type":"table"}
      // 2. URL: https://barclik.com/qr/local/123/table/5
      // 3. Simple: BC_123_T5 (BarClik_LocalId_TableId)
      
      if (qrData.startsWith('{')) {
        const data = JSON.parse(qrData);
        return data.localId && data.type;
      }
      
      if (qrData.includes('barclik.com/qr/')) {
        return true;
      }
      
      if (qrData.startsWith('BC_')) {
        const parts = qrData.split('_');
        return parts.length >= 2; // BC_localId mínimo
      }
      
      return false;
    } catch {
      return false;
    }
  }

  /**
   * Parsear datos del QR
   */
  parseQRData(qrData: string): QRData | null {
    try {
      // Formato JSON
      if (qrData.startsWith('{')) {
        return JSON.parse(qrData);
      }
      
      // Formato URL
      if (qrData.includes('barclik.com/qr/')) {
        const url = new URL(qrData);
        const pathParts = url.pathname.split('/');
        const localId = pathParts[3];
        const tableId = pathParts[5];
        
        return {
          localId,
          tableId,
          type: tableId ? 'table' : 'restaurant',
          version: '1.0'
        };
      }
      
      // Formato simple BC_localId_TtableId
      if (qrData.startsWith('BC_')) {
        const parts = qrData.split('_');
        const localId = parts[1];
        const tableId = parts[2]?.startsWith('T') ? parts[2].substring(1) : undefined;
        
        return {
          localId,
          tableId,
          type: tableId ? 'table' : 'restaurant',
          version: '1.0'
        };
      }
      
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Verificar conexión con POS del local
   */
  private checkPOSConnection(localId: string): Promise<boolean> {
    return this.http.get<{connected: boolean}>(`${this.API_URL}/restaurant/${localId}/pos-status`)
      .pipe(
        map(response => response.connected),
        catchError(() => of(false))
      ).toPromise() as Promise<boolean>;
  }

  /**
   * Procesar resultado del escaneo QR
   */
  private processQRResult(result: QRScanResult): QRScanResult {
    // Lógica adicional de procesamiento si es necesaria
    if (result.success && result.restaurant) {
      const features = result.restaurant.features;
      
      result.actions = {
        canViewMenu: features.menuEnabled,
        canOrderToBar: features.barOrderingEnabled,
        canOrderToTable: features.tableServiceEnabled,
        requiresGeolocation: features.geolocationRequired,
        requiresTableSelection: features.tableServiceEnabled && !result.table
      };
    }
    
    return result;
  }

  /**
   * Calcular distancia entre dos puntos
   */
  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371e3; // Radio de la Tierra en metros
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Buscar restaurantes por nombre
   */
  searchRestaurantsByName(query: string): Observable<any[]> {
    const headers = this.getAuthHeaders();
    return this.http.get<any>(`/api/v1/restaurants/search`, { 
      headers,
      params: { query }
    }).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data;
        }
        return [];
      }),
      catchError(error => {
        console.error('❌ Error searching restaurants:', error);
        // No fallback data - return empty array
        return of([]);
      })
    );
  }

  /**
   * Generar QR de prueba (para desarrollo)
   */
  generateTestQR(localId: string, tableId?: string): string {
    const qrData: QRData = {
      localId,
      tableId,
      type: tableId ? 'table' : 'restaurant',
      version: '1.0',
      timestamp: new Date().toISOString()
    };
    
    return JSON.stringify(qrData);
  }

  /**
   * Obtener menu por URL directa (api_url de search results)
   */
  getMenuByDirectUrl(apiUrl: string): Observable<MenuData> {
    const headers = this.getAuthHeaders();
    return this.http.get<any>(apiUrl, { headers }).pipe(
      map(response => {
        if (response.success && response.data) {
          return this.convertMenuApiToMenuData(response.data);
        }
        throw new Error(response.error || 'Menu no disponible');
      }),
      catchError(error => {
        console.error('Error getting menu by direct URL:', error);
        throw error;
      })
    );
  }

  /**
   * Obtener menu por hash de terminal
   */
  getMenuByTerminalHash(terminalHash: string): Observable<MenuData> {
    console.log(`🍽️ Getting menu for terminal hash: ${terminalHash}`);
    const headers = this.getAuthHeaders();
    return this.http.get<any>(`/api/v1/menu/terminal/${terminalHash}`, { headers })
      .pipe(
        map(response => {
          if (response.success && response.data) {
            const menuData = this.convertMenuApiToMenuData(response.data);
            
            // Enable real-time updates for this menu
            const companyId = parseInt(response.data.company_id);
            if (!isNaN(companyId)) {
              this.subscribeToRealTimeMenu(companyId);
            }
            
            // Update current menu subject
            this.currentMenuSubject.next(menuData);
            
            return menuData;
          } else {
            throw new Error(response.error || 'Menú no disponible para este terminal');
          }
        }),
        catchError(error => {
          console.error(`❌ Error getting menu for terminal ${terminalHash}:`, error);
          return of({
            id: '',
            restaurant_id: terminalHash,
            name: 'Menú no disponible',
            currency: 'EUR',
            language: 'es',
            categories: [],
            updated_at: new Date().toISOString(),
            version: 1
          });
        })
      );
  }

  /**
   * Obtener información del restaurante por hash de terminal
   */
  getRestaurantInfoByTerminal(terminalHash: string): Observable<RestaurantInfo> {
    console.log(`🏪 Getting restaurant info for terminal hash: ${terminalHash}`);
    const headers = this.getAuthHeaders();
    return this.http.get<any>(`/api/v1/restaurant/terminal/${terminalHash}`, { headers })
      .pipe(
        map(response => {
          if (response.success && response.data) {
            const restaurantData = response.data;
            return {
              id: restaurantData.company_id?.toString() || terminalHash,
              name: restaurantData.company_name || 'Restaurante',
              address: restaurantData.address || 'Dirección no disponible',
              location: {
                lat: restaurantData.latitude || 0,
                lng: restaurantData.longitude || 0
              },
              phone: restaurantData.phone,
              website: restaurantData.website,
              features: {
                menuEnabled: true,
                orderingEnabled: restaurantData.allow_orders || false,
                tableServiceEnabled: false,
                barOrderingEnabled: restaurantData.allow_orders || false,
                geolocationRequired: false,
                posIntegration: false
              },
              openingHours: {}
            } as RestaurantInfo;
          } else {
            throw new Error(response.error || 'Información del restaurante no disponible');
          }
        }),
        catchError(error => {
          console.error(`❌ Error getting restaurant info for terminal ${terminalHash}:`, error);
          throw error;
        })
      );
  }

  /**
   * Convertir respuesta de la API de menús al formato MenuData
   */
  private convertMenuApiToMenuData(menuApiData: any): MenuData {
    console.log('🔄 Converting API menu data:', menuApiData);
    
    // Create a flat products array for the component to use
    const allProducts: any[] = [];
    
    // Handle the new hierarchical structure where categories and products are separate arrays
    const categories: MenuCategory[] = (menuApiData.categories || []).map((cat: any) => {
      // Find products for this category by name (not ID)
      const categoryProducts = (menuApiData.products || []).filter((product: any) => 
        product.category === cat.name
      );

      console.log(`📂 Category "${cat.name}" (ID: ${cat.id}) has ${categoryProducts.length} products`);
      console.log(`📂 Category parent_id: ${cat.parent_id || 'null'}`);

      // Add products to the flat array
      const processedProducts = categoryProducts.map((product: any) => {
        const variants = Array.isArray(product.variants) && product.variants.length > 0 ? product.variants : undefined;
        return {
          id: product.id.toString(),
          name: product.name,
          description: product.description || '',
          price: product.price,
          category: cat.name,
          category_id: cat.id.toString(),
          allergens: product.allergens || product.allergies || [],
          dietary_info: product.dietary_info || [],
          available: product.available !== false,
          ingredients_optional: product.ingredients_optional || [],
          ingredients_extras: product.ingredients_extras || [],
          image: product.image_url || '',
          image_url: product.image_url || '',
          images: product.image_url ? [product.image_url] : [],
          stock: product.stock,
          preparation_time: undefined,
          popularity_score: undefined,
          calories: undefined,
          spice_level: undefined,
          customizations: [],
          original_price: undefined,
          tags: [],
          variants,
          has_variants: !!variants,
        } as any;
      });

      // Add to flat products array
      allProducts.push(...processedProducts);

      return {
        id: cat.id.toString(),
        name: cat.name,
        description: cat.description,
        display_order: cat.id,
        visible: true,
        parent_id: cat.parent_id || null, // Add parent_id for hierarchical structure
        items: processedProducts
      } as MenuCategory;
    });

    console.log(`✅ Converted ${categories.length} categories with ${allProducts.length} total products`);

    return {
      id: `menu_${menuApiData.company_id}`,
      restaurant_id: menuApiData.company_id.toString(),
      name: `Menú de ${menuApiData.company_name}`,
      description: `Menú actualizado de ${menuApiData.company_name}`,
      currency: menuApiData.currency,
      language: 'es',
      categories,
      products: allProducts,
      packs: menuApiData.packs || [],
      updated_at: menuApiData.sync_timestamp,
      version: parseInt(menuApiData.version) || 1
    } as MenuData;
  }
}